import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatDkk, getPaymentStatus } from "@/lib/site";

export function BetalingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [result, setResult] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let ticks = 0;
    const timer = setInterval(async () => {
      ticks += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setResult(res);
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          clearInterval(timer);
        } else if (ticks > 20) {
          setTimedOut(true);
          clearInterval(timer);
        }
      } catch {
        if (ticks > 20) {
          setTimedOut(true);
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId, clear]);

  return (
    <div
      data-testid="payment-success-page"
      className="flex min-h-[70vh] flex-col items-start justify-center px-6 pb-24 pt-32 lg:px-16"
    >
      {!result && !timedOut && (
        <div className="flex items-center gap-4">
          <Loader2 className="h-6 w-6 animate-spin text-forest" />
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-sage">
            Bekræfter betaling…
          </p>
        </div>
      )}
      {timedOut && !result && (
        <>
          <XCircle className="h-10 w-10 text-bark" />
          <h1 className="mt-6 font-serif text-4xl text-cream">Vi kunne ikke bekræfte betalingen</h1>
          <p className="mt-4 max-w-md text-sage">
            Hvis beløbet er trukket, kontakt os venligst — så finder vi din ordre.
          </p>
        </>
      )}
      {result && (
        <>
          <CheckCircle2 className="h-10 w-10 text-forest" />
          <h1 className="mt-6 font-serif text-4xl tracking-tight text-cream sm:text-5xl">
            Tak for din ordre{result.customer_name ? `, ${result.customer_name}` : ""}!
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-sage">
            Betalingen på <span className="text-pale">{formatDkk(result.amount_dkk)}</span> er
            gennemført.{" "}
            {result.shipping === "levering"
              ? "Vi kontakter dig snarest for at aftale levering."
              : result.shipping === "forsendelse"
                ? "Vi sender dine varer hurtigst muligt."
                : "Vi kontakter dig snarest for at aftale afhentning."}
          </p>
          {result.shipping === "afhentning" && (
            <div
              data-testid="pickup-address"
              className="mt-8 border border-forest/40 bg-forest/10 p-6"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-pale">
                Afhentningsadresse
              </p>
              <p className="mt-2 font-serif text-2xl text-cream">Fælleden 16, 7190 Billund</p>
            </div>
          )}
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-sage/60">
            Ordrereference: {result.session_id.slice(-12)}
          </p>
          <Link
            data-testid="success-home-link"
            to="/"
            className="mt-10 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink"
          >
            Tilbage til forsiden
          </Link>
        </>
      )}
    </div>
  );
}

export function BetalingCancel() {
  return (
    <div
      data-testid="payment-cancel-page"
      className="flex min-h-[70vh] flex-col items-start justify-center px-6 pb-24 pt-32 lg:px-16"
    >
      <XCircle className="h-10 w-10 text-bark" />
      <h1 className="mt-6 font-serif text-4xl tracking-tight text-cream sm:text-5xl">
        Betaling annulleret
      </h1>
      <p className="mt-5 max-w-md text-base leading-relaxed text-sage">
        Der er ikke trukket noget beløb. Din kurv er gemt, hvis du vil prøve igen.
      </p>
      <Link
        data-testid="cancel-home-link"
        to="/kasse"
        className="mt-10 rounded-full border border-white/25 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:border-pale hover:text-pale"
      >
        Tilbage til kassen
      </Link>
    </div>
  );
}
